// ★ VP — FUNÇÃO da pessoa na equipe (SDR / negociador) — puro.
//
// PAPEL é ACESSO: `papel` ∈ gestor | sdr | recepcao, gravado em claim + doc +
// usuarios_global só por callable, e é o que rules e functions consultam.
// FUNÇÃO é o que a pessoa FAZ no funil, e é múltipla: o negociador de produção
// é o gestor (dentista) e uma SDR pode negociar. Mora em `usuarios/{uid}.funcoes`
// como lista de 'sdr' | 'negociador', editável só pelo gestor (rules).
//
// UMA regra para TODAS as listas de pessoa: "SDR responsável", "Negociador",
// filtros, atribuição em massa, metas por pessoa, desempenho/placar. Quem lista
// gente para ESCOLHER importa daqui. A exceção documentada é quem lista gente
// por ACESSO ao espaço (`ListaConversas`, `DialogoTransferencia`: `PAPEIS_POR_ESPACO`)
// — ali a pergunta é "quem pode atender", não "quem faz o quê".

export const FUNCOES = ['sdr', 'negociador'];

/**
 * DEFAULT DERIVADO do papel, para quem ainda não tem `funcoes` gravado.
 * Reproduz exatamente as listas anteriores à VP (`papel sdr || gestor`): no dia
 * do deploy nada muda até o gestor marcar/desmarcar alguém. Sem migração.
 */
const DERIVADAS_DO_PAPEL = {
  gestor: ['sdr', 'negociador'],
  sdr: ['sdr'],
  recepcao: [],
};

/**
 * As funções de um usuário: a lista EXPLÍCITA vence (inclusive `[]` — gestor
 * desmarcado some das listas); ausente ou não-lista → derivado do papel
 * (dado corrompido não faz a pessoa sumir). Valores fora de FUNCOES caem, sem
 * duplicata, na ordem canônica.
 */
export function funcoesDe(usuario) {
  if (!usuario) return [];
  const explicitas = usuario.funcoes;
  if (Array.isArray(explicitas)) return FUNCOES.filter((f) => explicitas.includes(f));
  return [...(DERIVADAS_DO_PAPEL[usuario.papel] ?? [])];
}

export const temFuncao = (usuario, funcao) => funcoesDe(usuario).includes(funcao);

/** Quem da equipe exerce a função — só ATIVOS: quem saiu não recebe lead nem aparece em filtro. */
export const comFuncao = (equipe, funcao) =>
  (equipe ?? []).filter((u) => u?.ativo !== false && temFuncao(u, funcao));

export const apenasSdrs = (equipe) => comFuncao(equipe, 'sdr');
export const apenasNegociadores = (equipe) => comFuncao(equipe, 'negociador');

/** Metas por pessoa e desempenho: quem exerce QUALQUER função de venda (sdr ou negociador), ativo. */
export const vendedores = (equipe) =>
  (equipe ?? []).filter((u) => u?.ativo !== false && (temFuncao(u, 'sdr') || temFuncao(u, 'negociador')));

/**
 * A lista de um select MAIS o valor já gravado no lead quando ele não está entre
 * os elegíveis (perdeu a função, foi desativado, ou foi atribuído quando a lista
 * não filtrava). Nada é apagado nem forçado: a opção aparece marcada como
 * "(fora da função)" e o gestor troca quando quiser. Mesmo padrão do papel fora
 * da lista na tela de Equipe.
 */
export function opcoesComGravado(lista, idGravado, nomeGravado = '') {
  const base = lista ?? [];
  if (!idGravado || base.some((u) => u.id === idGravado)) return base;
  return [...base, { id: idGravado, nome: `${nomeGravado || idGravado} (fora da função)`, foraDaFuncao: true }];
}

/**
 * Importados da planilha têm o SDR só como TEXTO (`sdrNome`, sem `sdrId`): a ficha
 * mostra "(planilha: Nome)" ao lado do select, que segue "Sem responsável". Só
 * exibição — não grava, não tenta casar nome com usuário.
 */
export const nomeSdrDaPlanilha = (lead) => (!lead?.sdrId && lead?.sdrNome ? String(lead.sdrNome) : '');

/**
 * Quem PODE receber função de venda: só quem tem acesso ao funil (gestor, sdr).
 * Recepção não enxerga leads nem a fila de retorno — marcá-la SDR/negociadora
 * criaria uma responsável por algo que ela não vê. A tela trava as caixas com a
 * explicação; o caminho é trocar o acesso primeiro. (A regra de LISTA não olha
 * isto de propósito: um valor já gravado continua valendo.)
 */
export const podeTerFuncao = (usuario) => usuario?.papel === 'gestor' || usuario?.papel === 'sdr';

/**
 * ⚠⚠⚠ `ehSdrPorAcesso` VIVEU UM DIA E FOI REMOVIDA (10/out/2026) — o registro fica.
 *
 * ★ Ela nasceu no A-2 para o aviso *"N SDR(s) não recebem leads novos"* olhar o **papel**, com o
 * argumento de que `funcoesDe` deriva `sdr` para o gestor e ele apareceria todo dia.
 *
 * ⚠⚠ **O canário 4 derrubou o argumento:** o aviso acusou o **Joabe**, que tem acesso Vendas
 * (papel `sdr`) e função **só Negociador** — ★★★ e negociador **não entra no rodízio**.
 * ★ O papel responde *"que portas a pessoa abre"*; a pergunta do aviso é *"o que ela faz no
 * funil"*, e essa é a **função**. ⚠ Decisão do dono: `temFuncao(u, 'sdr')`.
 *
 * ★★ E o medo do gestor não se realizou, medido em produção: ⚠ ele tem `funcoes: []`
 * **explícito**, então a lista explícita vence a derivada e ele fica fora.
 */

/**
 * ★★ PC-6 — quantas pessoas ATIVAS têm o PAPEL `sdr` (acesso), não a função. Usado para a clínica
 * com UMA SDR só esconder os textos de reserva/trava (decisão do dono, 24/10). ⚠ Não é `apenasSdrs`:
 * a função `sdr` é derivada também para o gestor, e "só tem uma pessoa cadastrada" fala de SDRs.
 * Mora aqui porque decidir por `papel === 'sdr'` é privilégio deste módulo (guarda VP nº 1).
 */
export const quantasSdrsAtivas = (equipe) =>
  (equipe ?? []).filter((u) => u?.ativo !== false && u?.papel === 'sdr').length;
